import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import {
  CreateInspectionInstanceMutation,
  SourceType,
} from 'graphql-query-contracts';
import { createInspectionInstanceMutation } from 'graphql-queries';
import { CreateInspectionInstanceError } from './errors/CreateInspectionInstanceError';

export interface CreateInspectionInstanceInput {
  templateId: string;
  buildingId: string;
  buildingEntityType: string;
  sourceType: SourceType;
  tenantId?: string;
  tenantEntityType?: string;
  suiteId?: string;
  suiteEntityType?: string;
  assignedTo?: string;
  scheduledFor?: string;
  scheduleId?: string;
}

type InspectionInstance = CreateInspectionInstanceMutation['createInspectionInstance'];

type CreateInspectionInstanceMutationResult = {
  createInspectionInstance: InspectionInstance;
};

type CreateInspectionInstanceMutationVariables = {
  input: {
    templateId: string;
    buildingId: {
      sourceEntityId: string;
      sourceEntityType: string;
      sourceType: SourceType;
    };
    tenantId?: {
      sourceEntityId: string;
      sourceEntityType: string;
      sourceType: SourceType;
    };
    suiteId?: {
      sourceEntityId: string;
      sourceEntityType: string;
      sourceType: SourceType;
    };
    assignedTo?: string;
    scheduledFor?: string;
    scheduleId?: string;
  };
};

export interface UseCreateInspectionInstanceResult {
  createInstance: (input: CreateInspectionInstanceInput) => Promise<InspectionInstance>;
  loading: boolean;
  error: Error | undefined;
}

export const useCreateInspectionInstance = (): UseCreateInspectionInstanceResult => {
  const [mutateCreateInstance, { loading, error }] = useMutation<
    CreateInspectionInstanceMutationResult,
    CreateInspectionInstanceMutationVariables
  >(createInspectionInstanceMutation);

  const createInstance = useCallback(
    async (input: CreateInspectionInstanceInput): Promise<InspectionInstance> => {
      try {
        const variables: CreateInspectionInstanceMutationVariables = {
          input: {
            templateId: input.templateId,
            buildingId: {
              sourceEntityId: input.buildingId,
              sourceEntityType: input.buildingEntityType,
              sourceType: input.sourceType,
            },
            tenantId:
              input.tenantId && input.tenantEntityType
                ? {
                    sourceEntityId: input.tenantId,
                    sourceEntityType: input.tenantEntityType,
                    sourceType: input.sourceType,
                  }
                : undefined,
            suiteId:
              input.suiteId && input.suiteEntityType
                ? {
                    sourceEntityId: input.suiteId,
                    sourceEntityType: input.suiteEntityType,
                    sourceType: input.sourceType,
                  }
                : undefined,
            assignedTo: input.assignedTo,
            scheduledFor: input.scheduledFor,
            scheduleId: input.scheduleId,
          },
        };

        const result = await mutateCreateInstance({ variables });

        if (!result.data?.createInspectionInstance) {
          throw new CreateInspectionInstanceError('Failed to create inspection instance');
        }

        return result.data.createInspectionInstance;
      } catch (err) {
        const wrappedError = err instanceof CreateInspectionInstanceError
          ? err
          : new CreateInspectionInstanceError(err instanceof Error ? err.message : 'Failed to create inspection instance');
        throw wrappedError;
      }
    },
    [mutateCreateInstance]
  );

  return {
    createInstance,
    loading,
    error,
  };
};
